import React from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import {
  InputGroup, Input, Form, Card, CardBody, Label,
} from 'reactstrap';
import Button from 'app/components/general/Button';
import InputGroupBlock from 'app/components/general/InputGroupBlock';
import Alert from 'app/components/general/Alert';
import { Link } from 'react-router-dom';
import { friendlyErrorMessage } from 'app/utils/parsers';
import PasswordIcon from 'app/images/icons/password.svg';
import IActions from 'app/types/actions';
import validateInput from './ChangePasswordFormValidator';

interface IChangePasswordFormProps {
  actions?: IActions;
  email: string;
  isLoading?: boolean;
  hasLoaded?: boolean;
  hasFailed?: boolean;
  backendErrors: any;
}

interface IChangePasswordFormState {
  verificationCode?: string;
  email?: string;
  backendErrors?: any;
  errors: any;
  changed: boolean;
}

export default class ChangePasswordForm extends React.Component<
  IChangePasswordFormProps,
  IChangePasswordFormState
> {
  static defaultProps = { email: '', backendErrors: {} };

  state = {
    verificationCode: '',
    password: '',
    email: '',
    backendErrors: this.props.backendErrors || {},
    errors: {
      verificationCode: false,
    },
    changed: false,
  };

  componentDidMount = (): void => {
    this.props.actions.resetAuthenticateErrors();
  }

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed && nextProps.backendErrors !== prevState.backendErrors) {
      return { ...prevState, backendErrors: nextProps.backendErrors };
    }
    return null;
  }

  handleChange = (e): void => {
    delete this.state.errors[e.target.name];
    const state = {
      [e.target.name]: e.target.value,
      errors: this.state.errors,
      backendErrors: {},
      changed: true,
    };
    this.setState(state);
  };

  handleChangePassword = (e): void => {
    e.preventDefault();
    if (this.isValid()) {
      this.setState({ changed: false }, () => {
        this.props.actions.changePassword({
          email: this.props.email,
          verificationCode: this.state.verificationCode,
          password: this.state.password,
        });
      });
    }
  };

  isValid(): boolean {
    const { errors, isValid } = validateInput(this.state);

    if (!isValid) {
      this.setState({ errors });
    }

    return isValid;
  }

  renderBackendErrors(): React.ReactNode {
    if (
      !_.isEmpty(this.state.backendErrors?.message)
    ) {
      return (
        <Alert color="error">
          {friendlyErrorMessage(this.state.backendErrors.message)}
        </Alert>
      );
    }
    return null;
  }

  render(): React.ReactNode {
    const { errors } = this.state;
    return (
      <div>
        <div className="row">
          <div className="col">
            <Card className="extra-shadow">
              <CardBody className="with-extra-padding-2">
                <div className="row">
                  <div className="col">
                    <h2>Create your new password</h2>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <span className="text-black">
                      Enter the 6 digit code sent to
                      {' '}
                      {this.props.email}
                      &nbsp;to ensure your account is secure.
                    </span>
                  </div>
                </div>
                <div className="row with-margin-top">
                  <div className="col">
                    {this.renderBackendErrors()}
                    <Form autoComplete="off" onSubmit={this.handleChangePassword}>
                      <div className="row">
                        <div className="col">
                          <InputGroupBlock
                            inputGroupClassName={classnames('large', {
                              error: errors.verificationCode,
                            })}
                            icon={PasswordIcon}
                            type="text"
                            name="verificationCode"
                            placeholder="Verification code"
                            aria-label="Verification code"
                            autoFocus
                            tabIndex="1"
                            autoComplete="off"
                            value={this.state.verificationCode}
                            onBlur={this.handleChange}
                            onChange={this.handleChange}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <InputGroupBlock
                            inputGroupClassName={classnames('less-bottom-margin', 'large', {
                              error: errors.password,
                            })}
                            icon={PasswordIcon}
                            type="password"
                            name="password"
                            placeholder="New Password"
                            aria-label="New Password"
                            tabIndex="2"
                            autoComplete="new-password"
                            value={this.state.password}
                            onBlur={this.handleChange}
                            onChange={this.handleChange}
                          />
                          <Label className="text-caption text-grey">
                            Password must be at least 8 characters long, and contain
                            letters and numbers.
                          </Label>
                        </div>
                      </div>
                      <div className="row with-margin-top-10">
                        <div className="col">
                          <Button
                            color="primary"
                            className="btn-primary btn-full-width success-sm large"
                            tabIndex="3"
                            isLoading={this.props.isLoading}
                            hasSaved={this.props.hasLoaded}
                            hasFailed={this.props.hasFailed}
                            type="submit"
                          >
                            Change password
                          </Button>
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
        <div className="row justify-content-center sign-up">
          <div className="col-sm-5 text-sm-left text-center">
            <span className="d-inline-block text-grey">Don't have an account?</span>
            {' '}
            <Link to="/get-started">Sign up</Link>
          </div>
          <div className="col-sm-5 text-sm-right text-center">
            <span className="d-inline-block text-grey">Back to</span>
            {' '}
            <Link className="" to="/login">
              Log in
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
